/**
 * Map merge-field tag names (from backend registry) to user-facing labels.
 * Keep in sync with
 * law-firms-backend/src/templates/merge-fields/constants/merge-field-registry.ts.
 *
 * `recipient.*` / `lpr.*` are the canonical namespaces; `beneficiary.*` /
 * `executor.*` are retained as backward-compat aliases for firm templates that
 * still reference them. Both resolve identically on the backend, and either can
 * surface in a `missingRequired` list, so both must carry a label here.
 */
export const MERGE_FIELD_LABELS: Record<string, string> = {
  // Canonical per-recipient namespace
  'recipient.fullName': 'Beneficiary full name',
  'recipient.firstName': 'Beneficiary first name',
  'recipient.lastName': 'Beneficiary last name',
  'recipient.givenNames': 'Beneficiary given names',
  'recipient.surname': 'Beneficiary surname',
  'recipient.address': 'Beneficiary address',
  'recipient.dateOfBirth': 'Beneficiary date of birth',
  'recipient.phoneNumber': 'Beneficiary phone number',
  'recipient.emailAddress': 'Beneficiary email address',
  'recipient.bankAccountName': 'Beneficiary bank account name',
  'recipient.bankBsb': 'Beneficiary bank BSB',
  'recipient.bankAccountNumber': 'Beneficiary bank account number',
  // Canonical legal-personal-representative namespace
  'lpr.names': 'Executor names',
  'lpr.roleNoun': 'Executor role',
  // Matter / estate / deceased
  'estate.name': 'Estate name',
  'deceased.fullName': 'Deceased full name',
  'deceased.dateOfDeath': 'Date of death',
  'deceased.willDate': 'Will date',
  'deceased.lateOf': '"Late of" address',
  'deceased.placeOfDeath': 'Place of death',
  // Named as Manage Estate Details names it, so the blank points at the field to fill.
  'deceased.aliases': 'Other names',
  // The Queensland court forms (VALE-1993): the registry is chosen on the grant's notice step.
  'court.registry': 'Supreme Court registry',
  'court.publicationDate': 'Notice publication date',
  'court.publicTrusteeServedAt': 'Public Trustee service date',
  grantType: 'Grant type (Probate / Letters of Administration)',
  'matter.reference': 'Matter reference',
  // Grant of representation. Custom-bucket only on the backend, so these only
  // ever surface through a firm's own precedent — which is exactly why they
  // were missing here until a firm precedent reported one blank and a lawyer
  // read the raw tag `grant.issueDate`.
  'grant.issueDate': 'Grant issue date',
  'grant.applicationNumber': 'Grant application number',
  'grant.uniqueIdentifier': 'Grant unique identifier',
  // Family-provision window, derived per state from the matter's jurisdiction.
  'estate.beneficiaryNames': 'Beneficiary names',
  'estate.familyProvisionPeriod': 'Family provision period',
  'estate.familyProvisionRunsFrom': 'Family provision start point',
  'estate.familyProvisionExpiry': 'Family provision expiry date',
  // Estate-planning: the living client a will or power of attorney is drawn
  // for, and their spouse. Scalars only — a collection resolving empty is a
  // correct outcome and never reaches a missing-field list.
  'testator.fullName': 'Client full name',
  'testator.aliases': 'Client other names',
  'testator.address': 'Client address',
  'testator.occupation': 'Client occupation',
  'testator.dateOfBirth': 'Client date of birth',
  'partner.fullName': 'Partner full name',
  'partner.aliases': 'Partner other names',
  'partner.address': 'Partner address',
  'partner.occupation': 'Partner occupation',
  'partner.dateOfBirth': 'Partner date of birth',
  // Firm
  'lawFirm.name': 'Firm name',
  'lawFirm.address': 'Firm address',
  'lawyer.fullName': 'Solicitor name',
  // The solicitor who signs, or swears Queensland's Form 104: the acting
  // solicitor unless the firm signs as the author (VALE-1993).
  'signatory.fullName': 'Signing solicitor',
  'lawyer.signature': 'Solicitor signature image',
  // Auto-resolved
  date: 'Document date (auto-resolved)',
  signatureBlock: 'Signature block (auto-resolved)',
  // Backward-compat aliases (firm templates authored against the old names)
  'beneficiary.fullName': 'Beneficiary full name',
  'beneficiary.address': 'Beneficiary address',
  'executor.names': 'Executor names',
  'executor.roleNoun': 'Executor role'
};

/**
 * Where the lawyer should navigate to fix a missing field. Powers the
 * "fix location" hint on blocked-release chips.
 */
export type FieldFixLocation =
  | 'beneficiary'
  | 'matter'
  | 'contacts'
  | 'firm'
  | 'planning'
  | 'auto';

export const MERGE_FIELD_FIX_LOCATION: Record<string, FieldFixLocation> = {
  // Canonical per-recipient namespace — fixed on the beneficiary detail view
  'recipient.fullName': 'beneficiary',
  'recipient.firstName': 'beneficiary',
  'recipient.lastName': 'beneficiary',
  'recipient.givenNames': 'beneficiary',
  'recipient.surname': 'beneficiary',
  'recipient.address': 'beneficiary',
  'recipient.dateOfBirth': 'beneficiary',
  'recipient.phoneNumber': 'beneficiary',
  'recipient.emailAddress': 'beneficiary',
  'recipient.bankAccountName': 'beneficiary',
  'recipient.bankBsb': 'beneficiary',
  'recipient.bankAccountNumber': 'beneficiary',
  // Held on a contact, not on the matter: both resolve from the contacts
  // flagged as legal personal representative and the role set on them.
  'lpr.names': 'contacts',
  'lpr.roleNoun': 'contacts',
  // Matter-level
  'estate.name': 'matter',
  'deceased.fullName': 'matter',
  'deceased.dateOfDeath': 'matter',
  'deceased.willDate': 'matter',
  'deceased.lateOf': 'matter',
  'deceased.placeOfDeath': 'matter',
  'deceased.aliases': 'matter',
  grantType: 'matter',
  'matter.reference': 'matter',
  'grant.issueDate': 'matter',
  'grant.applicationNumber': 'matter',
  'grant.uniqueIdentifier': 'matter',
  // Derived from the matter's jurisdiction and its grant / date of death, so
  // the thing to fix is the matter data underneath, not the window itself.
  'estate.beneficiaryNames': 'contacts',
  'estate.familyProvisionPeriod': 'matter',
  'estate.familyProvisionRunsFrom': 'matter',
  'estate.familyProvisionExpiry': 'matter',
  // Estate planning — the facts live on the testator cards in the planning
  // workspace, NOT on Manage Estate Details, which a planning matter does not
  // have. Without these the default fallback below would send a solicitor to a
  // screen that is not on their matter at all.
  'testator.fullName': 'planning',
  'testator.aliases': 'planning',
  'testator.address': 'planning',
  'testator.occupation': 'planning',
  'testator.dateOfBirth': 'planning',
  'partner.fullName': 'planning',
  'partner.aliases': 'planning',
  'partner.address': 'planning',
  'partner.occupation': 'planning',
  'partner.dateOfBirth': 'planning',
  // Firm settings
  'lawFirm.name': 'firm',
  'lawFirm.address': 'firm',
  'lawyer.fullName': 'firm',
  'lawyer.signature': 'firm',
  // Auto-resolved
  date: 'auto',
  signatureBlock: 'auto',
  // Backward-compat aliases
  'beneficiary.fullName': 'beneficiary',
  'beneficiary.address': 'beneficiary',
  'executor.names': 'contacts',
  'executor.roleNoun': 'contacts'
};

/**
 * Field-specific fix hints, for the fields where naming the surface alone
 * doesn't tell the lawyer what to actually do there. Takes precedence over the
 * per-location hint below.
 */
const MERGE_FIELD_FIX_HINTS: Record<string, string> = {
  'court.registry': 'Choose it on the Grant Notice step',
  'court.publicationDate': 'Record it on the Grant Notice step',
  'court.publicTrusteeServedAt':
    'Record it under Earliest filing date on the Court Documents step',
  'signatory.fullName':
    'Set the acting solicitor in Manage Estate Details on the matter',
  'lpr.names': 'Fix from Matter Contacts by assigning an executor',
  'lpr.roleNoun': 'Fix from Matter Contacts by assigning an executor role',
  'executor.names': 'Fix from Matter Contacts by assigning an executor',
  'executor.roleNoun': 'Fix from Matter Contacts by assigning an executor role'
};

/**
 * Resolve a field tag to its display label, falling back to the raw tag
 * if we haven't mapped it (shouldn't happen in practice; defensive).
 */
export function getMergeFieldLabel(tag: string): string {
  return MERGE_FIELD_LABELS[tag] ?? humaniseMergeFieldTag(tag);
}

/**
 * A readable label for a tag nobody mapped: its last segment, split into words.
 *
 * The raw tag was the fallback, and it reached a lawyer as
 * `statement.reimbursementsTotal` in a sentence written for people. A tag missing from the map is
 * a gap here, but the reader should get "Reimbursements total", not the gap.
 */
function humaniseMergeFieldTag(tag: string): string {
  const last = tag.split('.').pop() ?? tag;
  const words = last.replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * Short hint about where the lawyer should fix this missing field.
 */
/**
 * Whether Vale knows where this field is recorded. A surface that cannot point anywhere real
 * should say nothing, rather than a generic instruction the reader cannot act on.
 */
export function hasMergeFieldFixLocation(tag: string): boolean {
  return tag in MERGE_FIELD_FIX_HINTS || tag in MERGE_FIELD_FIX_LOCATION;
}

export function getMergeFieldFixHint(tag: string): string {
  const specific = MERGE_FIELD_FIX_HINTS[tag];
  if (specific) return specific;

  const location = MERGE_FIELD_FIX_LOCATION[tag];
  switch (location) {
    // Unmapped: say nothing about where it lives rather than guess. The guess used to be
    // "Manage Estate Details", which sent a lawyer to a screen that holds none of the account's
    // figures when a statement subtotal came back blank.
    case undefined:
      return 'Add the missing detail to the matter, or adjust the document in Word';
    case 'beneficiary':
      return 'Fix on the beneficiary detail view';
    case 'matter':
      return 'Fix in Manage Estate Details on the matter';
    case 'contacts':
      return 'Fix from Matter Contacts';
    case 'firm':
      return 'Admin must update firm settings';
    case 'planning':
      return 'Fix on the client’s card in the planning workspace';
    case 'auto':
      return 'Auto-resolved, should not be reported missing';
  }
}
